import React, { PureComponent } from "react";
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { mapIsEmpty } from 'utils/helpers';
import Table from "components/Table";
import Button from 'components/Button';
import PopoverLinklist from 'components/Popover/PopoverLinklist';
import H1 from 'components/H1';
import Icon from 'components/Icon';
import Tooltip from "components/Tooltip";
import {
  Wrapper,
  Title,
  StyledEmptyList,
  EmptyText,
  HeaderFilter,
  RowHyperlink,
  ButtonWrapper,
} from "./styledComponents";
import { ButtonLink } from 'components/Button/ButtonLink';
import Filter from "./Filter";
import Spinner from 'components/Spinner';
import { selectUser, selectFilterForm, selectAllLeads, selectFetching, selectPagingObject } from "../selectors";
import { fetchLeadsRequest, downLoadLeadRequest } from "../actions";
import moment from 'moment';

const columns = (userType) => {
  const allowed = ["pro", "enterprise_admin", "super_admin"].includes(userType);
  return [
    {
      header: "first",
      path: "first_name",
      type: "name",
      width: "100px",
      customRow: (rowData) => {
        const { first_name } = rowData;
        
        const shouldTruncate = first_name.length > 12;
        return shouldTruncate ? (
          <Tooltip
            text={first_name}
            weight="normal"
            size="14px"
            width="150px"
            casing="none"
            position="bottom"
          >
            {`${first_name.slice(0, 12)}...`}
          </Tooltip>
        ) : (
          `${first_name}`
        );
      },
    },
    {
      header: "last",
      path: "last_name",
      type: "name",
      width: "100px",
      customRow: (rowData) => {
        const { last_name } = rowData;
        
        const shouldTruncate = last_name.length > 12;
        return shouldTruncate ? (
          <Tooltip
            text={last_name}
            weight="normal"
            size="14px"
            width="150px"
            casing="none"
            position="bottom"
          >
            {`${last_name.slice(0, 12)}...`}
          </Tooltip>
        ) : (
          `${last_name}`
        );
      },
    },
    {
      header: "email",
      path: "email",
      width: "200px",
      type: "email",
      customRow: (rowData) => {
        const shouldTruncate = rowData.email.length > 25;
        return (
          <RowHyperlink href={`mailto:${rowData.email}`}>
            {" "}
            {shouldTruncate ? (
              <Tooltip
                text={rowData.email}
                weight="normal"
                size="14px"
                width="250px"
                casing="none"
                position="bottom"
              >
                {` ${rowData.email.slice(0, 25)}...`}
              </Tooltip>
            ) : (
              rowData.email
            )}
          </RowHyperlink>
        );
      },
    },
    { width: "120px", header: "phone", path: "phone", type: "phone" },
    {
      width: "125px",
      header: "twitter",
      path: "twitter",
      type: "twitter",
      customRow: (rowData) => {
        let shouldTruncate = rowData.twitter.length > 20;

        return (
          <RowHyperlink
            onClick={() =>
              window.open(`https://twitter.com/${rowData.twitter}`)
            }
          >
            {shouldTruncate ? (
              <Tooltip
                text={`${rowData.twitter}`}
                weight="normal"
                size="14px"
                width="250px"
                casing="none"
                position="bottom"
              >
                {`@${rowData.twitter.slice(0, 20)}...`}
              </Tooltip>
            ) : rowData.twitter ? (
              `@${rowData.twitter}`
            ) : (
              ""
            )}
          </RowHyperlink>
        );
      },
    },
    allowed && {
      width: "110px",
      header: "dob",
      path: "birthday",
      type: "birthday",
    },
    allowed && { width: "100px", header: "zip", path: "zipcode", type: "zip" },
    { width: "110px", header: "last seen", path: "time_shared", type: "date" },
    {
      width: "60px",
      header: "",
      type: "dropdown",
      customRow: (rowData) => (
        <PopoverLinklist links={PopoverLinks(rowData.channel.id)} />
      ),
    },
  ];
};
const PopoverLinks = (id) => {
  const url = `https://staging.mvbth.com/ch/${id}`;
  const items = [
    {
      title: 'View Gallery',
      onClick: () => window.open(url)
    }
  ]
  return items;
}

const EmptyList = () => (
  <StyledEmptyList>
    <Icon
      className='channel-list__empty-icon'
      size='140'
      name='channels'
    />
    <EmptyText>
      It's looking empty around here…
    </EmptyText>
  </StyledEmptyList>
);

class LeadsList extends PureComponent {
  state = {
    sort: {},
    page: 1,
  };

  componentDidMount() {
    const { match: { params: { channelId } } } = this.props;
    if (!["lite",""].includes(this.props.user.permission_role)) {
      this.props.fetchLeads(this.getFilter(channelId));
    }
  }

  componentDidUpdate(prevProps) {
    const {
      match: {
        params: { channelId },
      },
    } = this.props;
    if (
      prevProps.user.permission_role === "" &&
      prevProps.user.permission_role !== this.props.user.permission_role
    ) {
      if (this.props.user.permission_role !== "lite") {
        this.props.fetchLeads(this.getFilter(channelId));
      }
    }
  }

  handleFilter = () => this.props.fetchLeads(this.getFilter());

  handlePageChange = (page) => {
    this.props.fetchLeads({
      ...this.getFilter(),
      page,
    });
  };

  handleSortChanged = (sortObj) => {
    this.setState({ sort: sortObj }, () => {
      this.props.fetchLeads(this.getFilter());
    });
  };

  getFilter = (channelId = null) => {
    const { filterForm } = this.props;
    const dateRange = filterForm.dateRange;

    return {
      page: this.props.paging.currentPage,
      start_date: dateRange ? moment(dateRange.startDate).format() : null,
      end_date: dateRange ? moment(dateRange.endDate).format() : null,
      source_channel: channelId ? channelId : filterForm.sourceChannel,
      search_term: filterForm.search,
      sort_name: this.state.sort.path,
      sort_direction: this.state.sort.isDesc ? 'desc' : 'asc',
    };
  }

  render() {
    const { isFetching, user, filterForm, leads, downloadLeads, paging } = this.props;
    const { permission_role } = user;
    const isEmpty = mapIsEmpty(leads);
    const showTable = permission_role !== "lite" && !isFetching && !isEmpty;
    const data = !isEmpty ? Object.keys(leads).map(k => leads[k]) : null;

    return (
      <Wrapper>
        <Title>
          <HeaderFilter>
            <H1>Leads</H1>
            <Filter
              formData={filterForm}
              filterSubmit={this.handleFilter}
              match={this.props.match}
            />
          </HeaderFilter>
          {permission_role !== "lite" && showTable && (
            <ButtonWrapper>
              <Button onClick={() => downloadLeads(this.getFilter())}>Download CSV</Button>
            </ButtonWrapper>
          )}
        </Title>
        {permission_role === "lite" ? (
          <div style={{ marginTop: "2em", color: "white" }}>
            <ButtonWrapper>
              <ButtonLink
                href="mailto:sales@movebooth.com?&subject=Movebooth%20Account%20Upgrade%20Request&body=I'd like to upgrade to collect leads data from Movebooth!"
                rel="noreferrer noopener"
                target="_blank"
                type="button"
              >
                Upgrade to PLUS for Leads
              </ButtonLink>
            </ButtonWrapper>
          </div>
        ) : isFetching && !data ? (
          <Spinner />
        ) : isEmpty ? (
          <EmptyList />
        ) : (
          <Table
            columns={columns(permission_role).filter((x) => x)}
            data={data}
            isFetching={isFetching}
            {...paging}
            onPageChange={this.handlePageChange}
            sortChanged={this.handleSortChanged}
            isLeadsTable={true}
          />
        )}
      </Wrapper>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  user: selectUser(),
  isFetching: selectFetching(),
  filterForm: selectFilterForm(),
  leads: selectAllLeads(),
  paging: selectPagingObject(),
});

const mapDispatchToProp = (dispatch) => ({
  fetchLeads: (params) => dispatch(fetchLeadsRequest(params)),
  downloadLeads: (params) => dispatch(downLoadLeadRequest(params)),
});

export default connect(mapStateToProps, mapDispatchToProp)(LeadsList);



// WEBPACK FOOTER //
// ./src/containers/LeadsPage/LeadsList/index.js